// ./src/contexts/UserNotificationsContext/UserNotificationsContext.tsx
import React, {
  createContext,
  useContext,
  useMemo,
  useCallback,
} from "react";

import { UserNotification } from "@/utils/api/UserNotificationService";

import {
  useUserNotifications,
  UseUserNotificationsOptions,
} from "@/hooks/useUserNotifications";

import { useVisibilityAwareInterval } from "@/contexts/PageVisibilityContext";

// ============================================
// Constants
// ============================================

const REFRESH_INTERVAL_MS = 60 * 1000; // 60 seconds when active
const REFRESH_INTERVAL_INACTIVE_MS = 10 * 60 * 1000; // 10 minutes when inactive

// ============================================
// Types
// ============================================

export interface UserNotificationsContextValue {
  // Data
  notifications: UserNotification[];
  unreadNotifications: UserNotification[];
  readNotifications: UserNotification[];

  // Counts
  totalCount: number;
  unreadCount: number;

  // State
  loading: boolean;
  isUpdating: boolean;
  lastError: string | null;

  // Actions
  refresh: () => Promise<void>;
  markAsRead: (notificationId: string) => Promise<UserNotification | null>;
  markAllAsRead: () => Promise<boolean>;

  // Lookup
  getNotificationById: (id: string | undefined) => UserNotification | undefined;

  reset: () => void;
}

export interface UserNotificationsProviderProps {
  children: React.ReactNode;
  options?: Partial<UseUserNotificationsOptions>;
}

// ============================================
// Context
// ============================================

const UserNotificationsContext =
  createContext<UserNotificationsContextValue | null>(null);

// ============================================
// Hook
// ============================================

export const useUserNotificationsContext =
  (): UserNotificationsContextValue => {
    const context = useContext(UserNotificationsContext);
    if (!context) {
      throw new Error(
        "useUserNotificationsContext must be used within a UserNotificationsProvider"
      );
    }
    return context;
  };

// ============================================
// Provider
// ============================================

export const UserNotificationsProvider: React.FC<
  UserNotificationsProviderProps
> = ({ children, options = {} }) => {
  // Merge default options
  const mergedOptions: UseUserNotificationsOptions = {
    autoLoad: true,
    initialFilters: {},
    pageSize: 50,
    initialSort: [{ name: "createdAt", order: "DESC" }],
    ...options,
  };

  const {
    userNotifications,
    loading,
    isUpdating,
    lastError,
    unreadCount,
    refresh,
    markAsRead,
    markAllAsRead,
    getUserNotificationById,
    getUnreadNotifications,
    reset,
  } = useUserNotifications(mergedOptions);

  // ============================================
  // Filtered lists
  // ============================================

  const unreadNotifications = useMemo(
    () => getUnreadNotifications(),
    [getUnreadNotifications]
  );

  const readNotifications = useMemo(
    () => userNotifications.filter((n) => n.readAt),
    [userNotifications]
  );

  // ============================================
  // Counts
  // ============================================

  const totalCount = useMemo(
    () => userNotifications.length,
    [userNotifications]
  );

  // ============================================
  // Lookup wrapper
  // ============================================

  const getNotificationById = useCallback(
    (id: string | undefined): UserNotification | undefined => {
      return getUserNotificationById(id);
    },
    [getUserNotificationById]
  );

  // ============================================
  // Auto-refresh interval (visibility-aware)
  // ============================================

  // The hook intelligently manages intervals:
  // - When active: refreshes every 60 seconds
  // - When inactive: refreshes every 10 minutes
  // - On returning to active: does NOT immediately refresh,
  //   instead waits for the remaining time of the current interval
  useVisibilityAwareInterval(
    ({ isActive: _isActive, isReturningToActive: _isReturningToActive, inactiveDurationMs: _inactiveDurationMs }) => {
      // Can customize behavior based on state:
      // - isActive: whether app is currently active
      // - isReturningToActive: true only on first tick after returning from inactive
      // - inactiveDurationMs: how long user was away (when isReturningToActive is true)
      //
      // Example:
      // if (isReturningToActive && inactiveDurationMs > 5 * 60 * 1000) {
      //   // User was away for more than 5 minutes - do full refresh
      //   fullRefresh();
      // } else {
      //   refresh();
      // }
      refresh();
    },
    REFRESH_INTERVAL_MS,
    { inactiveIntervalMs: REFRESH_INTERVAL_INACTIVE_MS }
  );

  // ============================================
  // Context value
  // ============================================

  const value = useMemo<UserNotificationsContextValue>(
    () => ({
      // Data
      notifications: userNotifications,
      unreadNotifications,
      readNotifications,

      // Counts
      totalCount,
      unreadCount,

      // State
      loading,
      isUpdating,
      lastError,

      // Actions
      refresh,
      markAsRead,
      markAllAsRead,

      // Lookup
      getNotificationById,

      reset,
    }),
    [
      userNotifications,
      unreadNotifications,
      readNotifications,
      totalCount,
      unreadCount,
      loading,
      isUpdating,
      lastError,
      refresh,
      markAsRead,
      markAllAsRead,
      getNotificationById,
      reset,
    ]
  );

  return (
    <UserNotificationsContext.Provider value={value}>
      {children}
    </UserNotificationsContext.Provider>
  );
};